import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { invoices as api, payments as payApi, getSettings, sendInvoiceEmail, setInvoiceStatus, apiError, invoicePdfUrl } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Loading, Card, ConfirmDelete } from "@/components/Shared";
import { DocumentA4 } from "@/components/DocumentA4";
import { StatusBadge } from "@/components/StatusBadge";
import { ShareButton } from "@/components/ShareButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Printer, Mail, MessageCircle, Pencil, Wallet, Trash2, Send, Ban, RefreshCw, Bell } from "lucide-react";

export default function InvoiceView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [settings, setSettings] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: 0, method: "EFT", reference: "", date: new Date().toISOString().slice(0, 10), notes: "" });

  const load = () => Promise.all([api.get(id), getSettings()]).then(([d, s]) => { setDoc(d); setSettings(s); setPay((p) => ({ ...p, amount: Math.max(d.total - d.amount_paid, 0) })); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);

  if (!doc) return <Loading />;

  const balance = Math.max(doc.total - (doc.amount_paid || 0), 0);
  const waNumber = (doc.customer?.phone || settings?.whatsapp || "").replace(/\D/g, "");
  const waMsg = encodeURIComponent(`Hi ${doc.customer?.name || ""}, please find your invoice ${doc.number} from ${settings?.company_name || "WiseAce"} for ${money(doc.total)}. Balance due: ${money(balance)}.`);
  const mailto = `mailto:${doc.customer?.email || ""}?subject=${encodeURIComponent(`Invoice ${doc.number}`)}&body=${waMsg}`;

  const recordPayment = async (e) => {
    e.preventDefault();
    if (pay.amount <= 0) return toast.error("Enter a valid amount.");
    try {
      await payApi.create({ invoice_id: id, ...pay, amount: Number(pay.amount) });
      toast.success("Payment recorded");
      setPayOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const delPayment = async (pid) => { try { await payApi.remove(pid); toast.success("Payment removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  const sendEmail = async () => {
    try { const r = await sendInvoiceEmail(id); toast.success(r.message || "Invoice email sent"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cancelInvoice = async () => {
    try { await setInvoiceStatus(id, "cancelled"); toast.success("Invoice cancelled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 no-print">
        <button onClick={() => nav("/invoices")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B132B]" data-testid="view-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex flex-wrap gap-2">
          <a href={invoicePdfUrl(id)} download data-testid="print-invoice"><Button variant="outline"><Printer className="w-4 h-4 mr-1.5" /> Download PDF</Button></a>
          <ShareButton docType="invoice" docId={id} />
          <a href={mailto} data-testid="email-invoice"><Button variant="outline"><Mail className="w-4 h-4 mr-1.5" /> Email</Button></a>
          <Button variant="outline" onClick={sendEmail} data-testid="send-invoice-email"><Send className="w-4 h-4 mr-1.5" /> Send via WiseAce</Button>
          {waNumber && <a href={`https://wa.me/27${waNumber.replace(/^0/, "")}?text=${waMsg}`} target="_blank" rel="noreferrer" data-testid="whatsapp-invoice"><Button variant="outline" className="text-emerald-600 border-emerald-200"><MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp</Button></a>}
          <Button variant="outline" onClick={() => nav(`/invoices/${id}/edit`)} data-testid="edit-invoice-view"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
          {doc.display_status !== "paid" && doc.display_status !== "cancelled" && (
            <ConfirmDelete onConfirm={cancelInvoice} title="Cancel this invoice?" description="Cancelled invoices stop receiving reminders and are excluded from outstanding totals." trigger={<Button variant="outline" className="text-slate-600" data-testid="cancel-invoice"><Ban className="w-4 h-4 mr-1.5" /> Cancel</Button>} />
          )}
          {balance > 0 && doc.display_status !== "cancelled" && <Button onClick={() => setPayOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="record-payment-button"><Wallet className="w-4 h-4 mr-1.5" /> Record Payment</Button>}
        </div>
      </div>

      <DocumentA4 doc={doc} settings={settings} title="INVOICE" statusValue={doc.display_status} />

      {doc.payments?.length > 0 && (
        <Card className="max-w-[794px] mx-auto p-5 no-print" data-testid="payments-list">
          <h3 className="font-display font-semibold text-[#0B132B] mb-3">Payment History</h3>
          <div className="divide-y divide-slate-100">
            {doc.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`payment-item-${p.id}`}>
                <div><span className="font-medium text-[#0B132B]">{money(p.amount)}</span><span className="text-slate-400"> · {p.method} · {fmtDate(p.date)}</span>{p.reference && <span className="text-slate-400"> · Ref {p.reference}</span>}</div>
                <ConfirmDelete onConfirm={() => delPayment(p.id)} title="Remove payment?" trigger={<Button variant="ghost" size="icon" className="text-rose-500 h-8 w-8" data-testid={`delete-payment-${p.id}`}><Trash2 className="w-4 h-4" /></Button>} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {(doc.recurring || doc.reminders?.length > 0 || doc.emails?.length > 0) && (
        <div className="max-w-[794px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 no-print mt-2">
          {doc.recurring && (
            <Card className="p-5 md:col-span-2" data-testid="recurring-info">
              <div className="flex items-center gap-2 mb-2"><RefreshCw className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-display font-semibold text-[#0B132B]">Recurring Schedule</h3></div>
              <p className="text-sm text-slate-600">Generated from <button className="font-semibold text-[#0B132B] hover:text-[#D4AF37]" onClick={() => nav(`/recurring/${doc.recurring.id}`)}>{doc.recurring.title}</button> · {doc.recurring.frequency} · next {fmtDate(doc.recurring.next_invoice_date)}</p>
            </Card>
          )}
          <Card className="p-5" data-testid="reminder-history">
            <div className="flex items-center gap-2 mb-3"><Bell className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-display font-semibold text-[#0B132B]">Reminder History</h3></div>
            {doc.reminders?.length ? (
              <div className="space-y-2.5">
                {doc.reminders.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`reminder-${r.reminder_type}`}>
                    <div><span className="capitalize font-medium text-[#0B132B]">{r.reminder_type} reminder</span><div className="text-xs text-slate-400">{r.sent_at ? `Sent ${fmtDate(r.sent_at)}` : `Scheduled ${fmtDate(r.scheduled_date)}`}</div></div>
                    <StatusBadge status={r.status === "sent" ? "sent_status" : r.status} />
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">No reminders sent yet.</p>}
          </Card>
          <Card className="p-5" data-testid="email-history">
            <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-display font-semibold text-[#0B132B]">Email History</h3></div>
            {doc.emails?.length ? (
              <div className="space-y-2.5">
                {doc.emails.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-sm" data-testid={`email-log-${e.id}`}>
                    <div className="min-w-0"><div className="truncate text-[#0B132B]">{e.subject}</div><div className="text-xs text-slate-400">{e.to} · {fmtDate(e.created_at)}</div></div>
                    <StatusBadge status={["sent", "delivered"].includes(e.status) ? "sent_status" : e.status === "queued" ? "pending" : e.status} />
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">No emails sent yet.</p>}
          </Card>
        </div>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Record Payment · {doc.number}</DialogTitle></DialogHeader>
          <form onSubmit={recordPayment} className="space-y-4">
            <div className="bg-[#F1F5F9] rounded-lg p-3 text-sm flex justify-between"><span className="text-slate-500">Balance due</span><span className="font-semibold text-[#0B132B]">{money(balance)}</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (R) *</Label><Input type="number" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1.5" data-testid="payment-amount-input" /></div>
              <div>
                <Label>Method</Label>
                <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["EFT", "Cash", "Card", "Cheque", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Reference</Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="mt-1.5" data-testid="payment-reference-input" /></div>
              <div><Label>Date</Label><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className="mt-1.5" data-testid="payment-date-input" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-payment-button">Record Payment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
